import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Building2,
  Plus,
  Trash2,
  Edit3,
  Loader2,
  Search,
  Users,
  Box,
  Image,
  Factory,
  Package,
  GraduationCap
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_TYPE_LABELS = {
  commercial_industrial: { 
    label: 'Commercial / Industrial', 
    icon: Factory,
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
    description: 'Full access to all features'
  },
  surplus_equipment: { 
    label: 'Surplus Equipment', 
    icon: Package,
    color: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
    description: 'Tags and Guided Tours only'
  },
  training: { 
    label: 'Training', 
    icon: GraduationCap,
    color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
    description: 'Assets, Training Modules & Maintenance'
  },
  sato_canada: { 
    label: 'Sato Canada', 
    icon: GraduationCap,
    color: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
    description: 'Training with Quizzes, Hotspots & Modules'
  },
};

export function ClientManagementPanel({ isOpen, onClose }) {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newClient, setNewClient] = useState({
    name: '',
    logo_url: '',
    client_type: 'commercial_industrial',
    description: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  // Fetch clients
  const fetchClients = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen, fetchClients]);

  // Filter clients
  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Create client
  const handleCreate = async () => {
    if (!newClient.name) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newClient)
      });

      if (res.ok) {
        await fetchClients();
        setNewClient({
          name: '',
          logo_url: '',
          client_type: 'commercial_industrial',
          description: '',
          contact_email: '',
          contact_phone: '',
          address: ''
        });
        setIsCreateDialogOpen(false);
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to create client');
      }
    } catch (err) {
      console.error('Failed to create client:', err);
    } finally {
      setSaving(false);
    }
  };

  // Update client
  const handleUpdate = async () => {
    if (!editingClient) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/clients/${editingClient.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editingClient.name,
          logo_url: editingClient.logo_url,
          client_type: editingClient.client_type,
          description: editingClient.description,
          contact_email: editingClient.contact_email,
          contact_phone: editingClient.contact_phone,
          address: editingClient.address
        })
      });

      if (res.ok) {
        await fetchClients();
        setEditingClient(null);
      }
    } catch (err) {
      console.error('Failed to update client:', err);
    } finally {
      setSaving(false);
    }
  };

  // Delete client
  const handleDelete = async (clientId) => {
    if (!confirm('Are you sure you want to delete this client? This will affect all users and tours associated with it.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        await fetchClients();
      }
    } catch (err) {
      console.error('Failed to delete client:', err);
    }
  };

  const ClientTypeIcon = ({ type }) => {
    const config = CLIENT_TYPE_LABELS[type] || CLIENT_TYPE_LABELS.commercial_industrial;
    const Icon = config.icon;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Client Management
          </SheetTitle>
          <SheetDescription>
            Manage clients, their users, and tour assignments
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Search and Add */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-1">
              <Plus className="h-4 w-4" />
              Add Client
            </Button>
          </div>

          {/* Clients List */}
          <ScrollArea className="h-[calc(100vh-220px)]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No clients found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="p-4 rounded-lg border bg-card hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {client.logo_url ? (
                            <img 
                              src={client.logo_url} 
                              alt={client.name}
                              className="h-6 w-auto max-w-[80px] object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <h4 className="font-medium text-foreground truncate">
                              {client.name}
                            </h4>
                          )}
                          <Badge 
                            variant="outline" 
                            className={`text-[10px] ${CLIENT_TYPE_LABELS[client.client_type]?.color}`}
                          >
                            <ClientTypeIcon type={client.client_type} />
                            <span className="ml-1">{CLIENT_TYPE_LABELS[client.client_type]?.label}</span>
                          </Badge>
                        </div>
                        {client.logo_url && (
                          <p className="text-sm text-foreground mb-1">{client.name}</p>
                        )}
                        {client.contact_email && (
                          <p className="text-xs text-muted-foreground">{client.contact_email}</p>
                        )}
                        {client.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{client.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => setEditingClient({ ...client })}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleDelete(client.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Create Client Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Client</DialogTitle>
              <DialogDescription>
                Add a new client company to manage their tours and users
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  placeholder="Acme Corporation"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input
                  placeholder="https://example.com/logo.png"
                  value={newClient.logo_url}
                  onChange={(e) => setNewClient({ ...newClient, logo_url: e.target.value })}
                />
                {newClient.logo_url && (
                  <div className="p-2 border rounded bg-muted/50">
                    <img 
                      src={newClient.logo_url} 
                      alt="Logo preview"
                      className="h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.src = '';
                        e.target.alt = 'Invalid URL';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Client Type</Label>
                <Select 
                  value={newClient.client_type} 
                  onValueChange={(value) => setNewClient({ ...newClient, client_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commercial_industrial">
                      <div className="flex items-center gap-2">
                        <Factory className="h-4 w-4" />
                        <div>
                          <div>Commercial / Industrial</div>
                          <div className="text-xs text-muted-foreground">Full access to all features</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="surplus_equipment">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        <div>
                          <div>Surplus Equipment</div>
                          <div className="text-xs text-muted-foreground">Tags and Guided Tours only</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="training">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        <div>
                          <div>Training</div>
                          <div className="text-xs text-muted-foreground">Assets, Training Modules & Maintenance</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="sato_canada">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="h-4 w-4" />
                        <div>
                          <div>Sato Canada</div>
                          <div className="text-xs text-muted-foreground">Training with Quizzes, Hotspots & Modules</div>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of the client..."
                  value={newClient.description}
                  onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  placeholder="contact@company.com"
                  value={newClient.contact_email}
                  onChange={(e) => setNewClient({ ...newClient, contact_email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  placeholder="+1 234 567 8900"
                  value={newClient.contact_phone}
                  onChange={(e) => setNewClient({ ...newClient, contact_phone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  placeholder="123 Main St, City, Country"
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={!newClient.name || saving}
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Client Dialog */}
        <Dialog open={!!editingClient} onOpenChange={() => setEditingClient(null)}>
          <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Client</DialogTitle>
              <DialogDescription>
                Update client details
              </DialogDescription>
            </DialogHeader>
            
            {editingClient && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={editingClient.name}
                    onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Logo URL</Label>
                  <Input
                    value={editingClient.logo_url || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, logo_url: e.target.value })}
                  />
                  {editingClient.logo_url && (
                    <div className="p-2 border rounded bg-muted/50">
                      <img 
                        src={editingClient.logo_url} 
                        alt="Logo preview"
                        className="h-12 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Client Type</Label>
                  <Select 
                    value={editingClient.client_type} 
                    onValueChange={(value) => setEditingClient({ ...editingClient, client_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="commercial_industrial">Commercial / Industrial</SelectItem>
                      <SelectItem value="surplus_equipment">Surplus Equipment</SelectItem>
                      <SelectItem value="training">Training</SelectItem>
                      <SelectItem value="sato_canada">Sato Canada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editingClient.description || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, description: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={editingClient.contact_email || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, contact_email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={editingClient.contact_phone || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, contact_phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea
                    value={editingClient.address || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, address: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingClient(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

export default ClientManagementPanel;
